import { CollectionHelper } from 'src/app/helpers/collection-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import { SignHelper } from 'src/app/helpers/sign-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import { BoardTypeChoices } from 'src/app/model/product/board/board-part';
import { IPBoardPlacementTypeChoices } from 'src/app/model/product/ip-board/ip-board-placement-type';
import Product from 'src/app/model/product/product';
import { SUB_PROJECT_TYPES } from 'src/app/model/project/sub-project';
import { SignShapeTypesChoices } from 'src/app/model/sign/enums/sign-shape-type';
import { FormatPhoneNumber } from 'src/app/modules/app-pipes/format-phonenumber';
import { ImmoIPPlacerTourItemDetailHelper } from 'src/app/pages/immo-ip/placer/tour-item-detail/helper/immo-ip-placer-tour-item-detail-helper';
import { PrintAddressPipe } from 'src/app/pipes/print-address.pipe';
import { TourDetailIPBoardPart } from './model/tour-detail-ip-board-part';
import { TourDetailProduct } from './model/tour-detail-product';
import { TourDetailProject } from './model/tour-detail-project';
import { TourDetailSubProject } from './model/tour-detail-sub-project';
import { TourDetailTour } from './model/tour-detail-tour';
import { TourDetailTourItem } from './model/tour-detail-tour-item';

export class EmployeeTourEditModalHelper {
  private constructor() {}

  static processTour(
    tour: TourDetailTour,
    formatPhoneNumber: FormatPhoneNumber,
    printAddressPipe: PrintAddressPipe
  ): void {
    if (tour == null || !CollectionHelper.isPopulated(tour.tourItems)) {
      return;
    }
    tour.tourItems.forEach((ti) => {
      if (ti.subProject != null) {
        this.processSubProject(ti.subProject);
        ti.executed =
          ti.subProject.type === 'BaseProject'
            ? ti.subProject.project != null &&
              CollectionHelper.isPopulated(ti.subProject.project.products) &&
              ti.subProject.project.products[0] != null
              ? ti.subProject.project.products[0].executionComplete
              : null
            : ti.subProject.projectPlacement != null
            ? ti.subProject.projectPlacement.placementDone
            : null;

        const projectHasIPBoards =
          CollectionHelper.isPopulated(ti.subProject.project.products) &&
          ti.subProject.project.products.some((p) =>
            ProductHelper.isIPBoard(<Product>p)
          );
        if (projectHasIPBoards) {
          if (ti.subProject.projectPlacement.appointment) {
            const appointmentContactDisplay =
              ImmoIPPlacerTourItemDetailHelper.getAppointmentContactDisplay(
                ti.subProject.projectPlacement,
                formatPhoneNumber
              );

            ti.subProject.projectPlacement.appoinmentTooltip =
              StringHelper.join(
                [
                  appointmentContactDisplay,
                  ti.subProject.projectPlacement.needAppointmentInfo,
                ],
                '\n'
              );
          }
          if (ti.subProject.projectPlacement.collectKey) {
            if (ti.subProject.projectPlacement.collectKeyFromAddress != null) {
              ti.subProject.projectPlacement.collectKeyTooltip =
                printAddressPipe.transform(
                  ti.subProject.projectPlacement.collectKeyFromAddress.line1,
                  ti.subProject.projectPlacement.collectKeyFromAddress
                    .postalCode,
                  ti.subProject.projectPlacement.collectKeyFromAddress.city
                );
            } else {
              ti.subProject.projectPlacement.collectKeyTooltip =
                ti.subProject.projectPlacement.collectKeyExtraInfo;
            }
          }
        }
      }
      if (ti.immoTask != null) {
        ti.executed = ti.immoTask.executed;
      }
    });
  }

  private static processSubProject(subProject: TourDetailSubProject): void {
    this.calculateSubProjectTypeDisplays(subProject);
    if (subProject.project != null) {
      this.processProject(subProject.project);
    }
  }

  private static calculateSubProjectTypeDisplays(
    subProject: TourDetailSubProject
  ): void {
    if (subProject.type == null) {
      return;
    }
    subProject.typeClass = subProject.type.toLowerCase();
    switch (subProject.type) {
      case SUB_PROJECT_TYPES.BASE_PROJECT:
        subProject.typeDisplay = 'Plaatsen';
        break;
      case SUB_PROJECT_TYPES.ADJUSTMENT:
        subProject.typeDisplay = 'Aanpassing';
        break;
      case SUB_PROJECT_TYPES.SOLDRENTED:
        subProject.typeDisplay = 'Verkocht/Verhuurd';
        break;
      case SUB_PROJECT_TYPES.REPO:
        subProject.typeDisplay = 'Weghalen';
        break;
      default:
        throw new Error(`unknown subproject type: ${subProject.type}`);
    }
  }

  private static processProject(project: TourDetailProject): void {
    if (CollectionHelper.isPopulated(project.projectContacts)) {
      this.processProjectContacts(project);
    }
    if (CollectionHelper.isPopulated(project.products)) {
      this.processProducts(project.products);
    }
  }

  private static processProjectContacts(project: TourDetailProject): void {
    const companyNames = new Set<string>();
    project.projectContacts.forEach((pc) => {
      if (pc.company != null) {
        companyNames.add(pc.company.regularName);
      }
    });
    project.companyNames = [...companyNames];
  }

  private static processProducts(products: TourDetailProduct[]): void {
    products.forEach((p) => {
      if (p.boardPart != null) {
        p.isDivergentBoard =
          p.boardPart.boardType != null &&
          p.boardPart.boardType.ordinal === BoardTypeChoices.DIVERGENT.ordinal;
        p.typeDisplay = StringHelper.join(
          [
            p.boardPart.kind,
            this.getMeasurements(p.boardPart),
            p.boardPart.boardType == null ? null : p.boardPart.boardType.label,
            p.isDivergentBoard ? null : this.getPlacementType(p.boardPart),
            p.isDivergentBoard
              ? StringHelper.shortenText(p.boardPart.divergentBoardType)
              : null,
          ],
          ' '
        );
        if (p.isDivergentBoard) {
          p.divergentBoardDisplay = p.boardPart.divergentBoardType;
        }
      }
    });
  }

  private static getMeasurements(ipBoardPart: TourDetailIPBoardPart): string {
    if (ipBoardPart.sign != null) {
      return ipBoardPart.sign.signMeasurements == null
        ? null
        : SignHelper.getSignOrientationDisplayPipeable(
            ipBoardPart.sign.signMeasurements.width,
            ipBoardPart.sign.signMeasurements.height,
            ipBoardPart.sign.shape != null &&
              ipBoardPart.sign.shape.value ===
                SignShapeTypesChoices.UPRIGHT.value,
            false,
            false,
            false
          );
    } else {
      return StringHelper.join(
        [
          ipBoardPart.otherHeight == null
            ? null
            : ipBoardPart.otherHeight.toString(),
          ipBoardPart.otherWidth == null
            ? null
            : ipBoardPart.otherWidth.toString(),
        ],
        'x'
      );
    }
  }

  private static getPlacementType(ipBoardPart: TourDetailIPBoardPart): string {
    if (ipBoardPart == null) {
      return null;
    }
    return ipBoardPart.placementType == null ||
      ipBoardPart.placementType.ordinal ===
        IPBoardPlacementTypeChoices.OTHER.ordinal
      ? ipBoardPart.alternativePlacementType
      : ipBoardPart.placementType.label.toLowerCase();
  }

  static calculateTourItemsForMap(tour: TourDetailTour): TourDetailTourItem[] {
    if (tour == null || !CollectionHelper.isPopulated(tour.tourItems)) {
      return [];
    }
    const toReturn = tour.tourItems.map((ti) =>
      ti.subProject == null && ti.immoTask == null ? null : ti
    );
    CollectionHelper.filterInPlace(toReturn, (e) => e != null);
    return toReturn;
  }
}
